import React from 'react';
import { loggerUtils } from '../services/logger';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Link } from 'react-router';

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  errorId: string;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<{
    error: Error;
    errorInfo: React.ErrorInfo;
    resetError: () => void;
    errorId: string;
  }>;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: ''
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
      errorId: `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const errorId = `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Log the error with full context
    loggerUtils.error(error, 'react_error_boundary', {
      errorId,
      errorInfo: {
        componentStack: errorInfo.componentStack,
        errorBoundary: this.constructor.name
      },
      url: typeof window !== 'undefined' ? window.location.href : 'unknown',
      userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : 'unknown',
      timestamp: new Date().toISOString()
    });

    this.setState({
      error,
      errorInfo,
      errorId
    });

    // Also send to external error tracking if needed
    if (typeof window !== 'undefined') {
      // Could integrate with Sentry, LogRocket, etc. here
      // Error already logged by loggerUtils above
    }
  }

  resetError = () => {
    loggerUtils.userAction('error_boundary_reset', {
      errorId: this.state.errorId
    });

    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: ''
    });
  };

  render() {
    if (this.state.hasError && this.state.error) {
      const { fallback: Fallback } = this.props;

      if (Fallback && this.state.errorInfo) {
        return (
          <Fallback
            error={this.state.error}
            errorInfo={this.state.errorInfo}
            resetError={this.resetError}
            errorId={this.state.errorId}
          />
        );
      }

      return <DefaultErrorFallback 
        error={this.state.error}
        errorInfo={this.state.errorInfo}
        resetError={this.resetError}
        errorId={this.state.errorId}
      />;
    }

    return this.props.children;
  }
}

// Default error fallback component
function DefaultErrorFallback({ 
  error, 
  errorInfo, 
  resetError, 
  errorId 
}: {
  error: Error;
  errorInfo: React.ErrorInfo | null;
  resetError: () => void;
  errorId: string;
}) {
  const isDev = process.env.NODE_ENV === 'development';

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-2xl">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <div className="text-center">
            <AlertTriangle className="mx-auto h-16 w-16 text-red-500 mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Something went wrong
            </h1>
            <p className="text-gray-600 mb-6">
              We encountered an unexpected error. Our team has been notified.
            </p>

            <div className="flex justify-center space-x-4 mb-8">
              <button
                onClick={resetError}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Try Again
              </button>
              
              <Link
                to="/"
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Home className="w-4 h-4 mr-2" />
                Go Home
              </Link>
            </div>

            <div className="text-xs text-gray-500 mb-4">
              Error ID: <code className="bg-gray-100 px-1 rounded">{errorId}</code>
            </div>

            {isDev && (
              <details className="text-left mt-6 bg-red-50 rounded-lg p-4">
                <summary className="cursor-pointer font-medium text-red-800 mb-2">
                  Development Error Details
                </summary>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium text-red-800 mb-1">Error Message:</h4>
                    <code className="text-sm text-red-700 bg-red-100 px-2 py-1 rounded block whitespace-pre-wrap">
                      {error.message}
                    </code>
                  </div>
                  
                  {error.stack && (
                    <div>
                      <h4 className="font-medium text-red-800 mb-1">Stack Trace:</h4>
                      <code className="text-xs text-red-700 bg-red-100 px-2 py-1 rounded block whitespace-pre-wrap overflow-auto max-h-48">
                        {error.stack}
                      </code>
                    </div>
                  )}
                  
                  {errorInfo?.componentStack && (
                    <div>
                      <h4 className="font-medium text-red-800 mb-1">Component Stack:</h4>
                      <code className="text-xs text-red-700 bg-red-100 px-2 py-1 rounded block whitespace-pre-wrap overflow-auto max-h-48">
                        {errorInfo.componentStack}
                      </code>
                    </div>
                  )}
                </div>
              </details>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// HOC for wrapping components with error boundary
const _withErrorBoundary = function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  fallback?: React.ComponentType<{
    error: Error;
    errorInfo: React.ErrorInfo;
    resetError: () => void;
    errorId: string;
  }>
) {
  const WrappedComponent = (props: P) => (
    <ErrorBoundary fallback={fallback}>
      <Component {...props} />
    </ErrorBoundary>
  );

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;
  
  return WrappedComponent;
}

export default ErrorBoundary;